/**
 * Gemini Live API — client helpers (ephemeral token + PCM utilities).
 * Session WebSocket lifecycle is driven from {@link useGeminiLiveVoiceConversationEngine}.
 */

import firebaseApp from 'services/firebase';
import { ensureAnonymousFirebaseUser } from 'services/auth/ensureAnonymousAuth';
import {
  GEMINI_LIVE_TOKEN_CALLABLE,
  GEMINI_LIVE_MODEL_DEFAULT,
  resolveGeminiLiveFunctionsRegion,
} from './geminiLiveConstants';

/**
 * Prefetch cache for ephemeral tokens (see VoxPont latency Phase 1/2). The preflight
 * prewarm mints both direction tokens ahead of the user tapping Start; the live session
 * then consumes them with no network round trip. Entries are single-use and expire well
 * inside the server's 2-minute newSessionExpireTime window.
 */
const PREWARM_TTL_MS = 90_000;
/** @type {Map<string, { at: number, promise: Promise<Object> }>} */
const tokenPrefetchCache = new Map();

function tokenCacheKey({ model, translationConfig } = {}) {
  const resolvedModel = model || GEMINI_LIVE_MODEL_DEFAULT;
  const target = translationConfig?.targetLanguageCode?.trim?.() || '';
  const echo = translationConfig?.echoTargetLanguage === true ? '1' : '0';
  return `${resolvedModel}|${target}|${echo}`;
}

function takeFreshCachedToken(key) {
  const entry = tokenPrefetchCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.at > PREWARM_TTL_MS) {
    tokenPrefetchCache.delete(key);
    return null;
  }
  // Single-use: a minted token has uses:1, so remove it as soon as it is claimed.
  tokenPrefetchCache.delete(key);
  return entry.promise;
}

/**
 * Invoke the token callable (nearest deployed region) and return its data payload.
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
async function callTokenFunction(payload) {
  const region = resolveGeminiLiveFunctionsRegion();
  const fn = firebaseApp.functions(region).httpsCallable(GEMINI_LIVE_TOKEN_CALLABLE);
  try {
    const res = await fn(payload);
    return res.data;
  } catch (e) {
    const code = e?.code;
    const isLocal =
      typeof window !== 'undefined' && window.location?.hostname === 'localhost';
    if (
      isLocal &&
      (code === 'internal' ||
        code === 'functions/internal' ||
        (e?.message && /CORS|Failed to fetch|NetworkError/i.test(e.message)))
    ) {
      const projectId = firebaseApp?.options?.projectId || '(unknown)';
      const hint =
        `Gemini Live (beta) token call failed from localhost (${code || 'error'}). ` +
        `Confirm REACT_APP_FIREBASE_PROJECT_ID matches the project that hosts this function (current app projectId: ${projectId}). ` +
        `In Firebase Console → Authentication → Settings, add "localhost" to Authorized domains. ` +
        `In Google Cloud Console → APIs & Services → Credentials, open the Firebase Browser API key and under Application restrictions allow HTTP referrer http://localhost:3000/* (and your port). ` +
        `See Cloud Function logs for getGeminiLiveEphemeralToken if the request reaches the server.`;
      const wrapped = new Error(hint);
      wrapped.cause = e;
      wrapped.code = code;
      throw wrapped;
    }
    throw e;
  }
}

/**
 * Cloud callables require an authenticated Firebase user. Public pages (e.g. standalone
 * symptom checker) may have no user until we create an anonymous session. Delegates to the
 * shared helper, which waits for Firebase's async session restore before signing in (so an
 * existing user is reused instead of minting a duplicate on every call) and retries
 * transient failures with backoff.
 * @returns {Promise<{ uid: string } | null>}
 */
export async function ensureFirebaseUserForLiveVoice() {
  return ensureAnonymousFirebaseUser({
    logPrefix: '[GeminiLive]',
    errorName: 'GeminiLiveAuthError',
    blockedHint:
      'Enable Anonymous sign-in: Firebase Console → Authentication → Sign-in method → ' +
      'Anonymous → Enable, then reload. Gemini Live (beta) needs a Firebase user to call ' +
      'the token API on public pages.',
  });
}

/**
 * @param {{
 *   model?: string,
 *   signInAnonymouslyIfNeeded?: boolean,
 *   translationConfig?: { targetLanguageCode?: string, echoTargetLanguage?: boolean },
 * }} [options]
 * @returns {Promise<{ ephemeralToken: string, model: string, expireTime?: string }>}
 */
export async function fetchGeminiLiveEphemeralToken(options = {}) {
  // Consume a prewarmed token first (no network). On any failure of the cached
  // promise, fall through to a fresh mint so a bad prewarm never breaks startup.
  const cachedPromise = takeFreshCachedToken(tokenCacheKey(options));
  if (cachedPromise) {
    try {
      const cached = await cachedPromise;
      if (cached?.ephemeralToken && cached?.model) return cached;
    } catch {
      /* prewarm failed — mint fresh below */
    }
  }

  const allowAnon = options.signInAnonymouslyIfNeeded !== false;
  if (allowAnon) {
    await ensureFirebaseUserForLiveVoice();
  }
  const payload = {
    model: options.model || GEMINI_LIVE_MODEL_DEFAULT,
  };
  if (options.translationConfig?.targetLanguageCode) {
    payload.translationConfig = {
      targetLanguageCode: options.translationConfig.targetLanguageCode,
      echoTargetLanguage: options.translationConfig.echoTargetLanguage === true,
    };
  }
  const d = await callTokenFunction(payload);
  if (!d?.ephemeralToken || !d?.model) {
    throw new Error('Invalid Gemini Live (beta) token response from server');
  }
  return d;
}

/**
 * Mint several direction tokens in a single callable round trip (batch mode). Used by
 * the prewarm path so a whole session's tokens are acquired with one network request.
 *
 * @param {Array<{ targetLanguageCode?: string, echoTargetLanguage?: boolean }>} translationConfigs
 * @param {{ model?: string, signInAnonymouslyIfNeeded?: boolean }} [options]
 * @returns {Promise<Array<{ ephemeralToken: string, model: string, expireTime?: string }>>}
 */
export async function fetchGeminiLiveEphemeralTokens(translationConfigs, options = {}) {
  const configs = Array.isArray(translationConfigs) ? translationConfigs : [];
  if (!configs.length) return [];

  const allowAnon = options.signInAnonymouslyIfNeeded !== false;
  if (allowAnon) {
    await ensureFirebaseUserForLiveVoice();
  }
  const payload = {
    model: options.model || GEMINI_LIVE_MODEL_DEFAULT,
    translationConfigs: configs.map((tc) => ({
      targetLanguageCode: tc?.targetLanguageCode || '',
      echoTargetLanguage: tc?.echoTargetLanguage === true,
    })),
  };
  const d = await callTokenFunction(payload);
  if (!Array.isArray(d?.tokens)) {
    throw new Error('Invalid Gemini Live (beta) batch token response from server');
  }
  return d.tokens;
}

/**
 * Prewarm (mint + cache) the ephemeral tokens for a set of translate directions ahead
 * of session start. Best-effort: swallows errors so a failed prewarm never blocks or
 * breaks the live session (it simply mints normally at start). The cached tokens are
 * single-use and expire inside the server's new-session window.
 *
 * @param {Array<{ targetLanguageCode?: string, echoTargetLanguage?: boolean }>} translationConfigs
 * @param {{ model?: string }} [options]
 * @returns {Promise<void>}
 */
export async function prewarmGeminiLiveEphemeralTokens(translationConfigs, options = {}) {
  const configs = Array.isArray(translationConfigs) ? translationConfigs : [];
  if (!configs.length) return;

  const model = options.model;
  // Skip directions already warm; only mint the ones we actually need.
  const pending = configs.filter(
    (tc) => !tokenPrefetchCache.has(tokenCacheKey({ model, translationConfig: tc }))
  );
  if (!pending.length) return;

  const at = Date.now();
  // One batched promise resolves all pending tokens; each direction's cache entry
  // derives its own token from that shared promise (deduped, single round trip).
  // CareStart3 passes signInAnonymouslyIfNeeded:false (signed-in patients only).
  const batchPromise = fetchGeminiLiveEphemeralTokens(pending, {
    model,
    ...(options.signInAnonymouslyIfNeeded === false
      ? { signInAnonymouslyIfNeeded: false }
      : {}),
  }).catch((err) => {
    // Drop failed entries so a later fetch re-mints instead of awaiting a rejection.
    pending.forEach((tc) => tokenPrefetchCache.delete(tokenCacheKey({ model, translationConfig: tc })));
    throw err;
  });

  pending.forEach((tc, index) => {
    const key = tokenCacheKey({ model, translationConfig: tc });
    const promise = batchPromise.then((tokens) => {
      const token = tokens[index];
      if (!token?.ephemeralToken || !token?.model) {
        throw new Error('Invalid prewarmed Gemini Live token');
      }
      return token;
    });
    tokenPrefetchCache.set(key, { at, promise });
  });

  try {
    await batchPromise;
  } catch (err) {
    console.warn('[GeminiLive] token prewarm failed (will mint at start)', err?.message || err);
  }
}

/**
 * @param {Float32Array} buffer
 * @param {number} sampleRate
 * @param {number} outSampleRate
 * @returns {Float32Array}
 */
export function downsampleBuffer(buffer, sampleRate, outSampleRate) {
  if (outSampleRate === sampleRate) return buffer;
  const ratio = sampleRate / outSampleRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i += 1) {
      accum += buffer[i];
      count += 1;
    }
    result[offsetResult] = count ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}

/**
 * @param {Float32Array} float32Arr
 * @returns {Int16Array}
 */
export function floatTo16BitPCM(float32Arr) {
  const len = float32Arr.length;
  const int16 = new Int16Array(len);
  for (let i = 0; i < len; i += 1) {
    const s = Math.max(-1, Math.min(1, float32Arr[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16;
}

/**
 * Encode little-endian PCM16 for Gemini Live sendRealtimeInput (expects base64 + mimeType).
 * @param {Int16Array} int16Arr
 * @returns {string}
 */
export function encodeLittleEndianPcm16Base64(int16Arr) {
  const u8 = new Uint8Array(int16Arr.buffer, int16Arr.byteOffset, int16Arr.byteLength);
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < u8.length; i += chunk) {
    binary += String.fromCharCode.apply(null, u8.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Decode base64 little-endian PCM to an AudioBuffer for playback.
 * @param {string} base64
 * @param {number} sampleRate e.g. 24000 for Live output
 * @param {AudioContext} audioContext
 */
export function decodeLittleEndianPcm16Base64(base64, sampleRate, audioContext) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i += 1) {
    float32[i] = int16[i] / 0x8000;
  }
  const buffer = audioContext.createBuffer(1, float32.length, sampleRate);
  buffer.copyToChannel(float32, 0);
  return buffer;
}

/**
 * Parse sample rate from mime like "audio/pcm;rate=24000".
 * @param {string} [mimeType]
 * @param {number} fallback
 */
export function parsePcmRateFromMime(mimeType, fallback) {
  if (!mimeType || typeof mimeType !== 'string') return fallback;
  const m = /rate=(\d+)/i.exec(mimeType);
  return m ? parseInt(m[1], 10) : fallback;
}
